"use client";

import { createOtpAuthProvider } from "@gogive/auth/react";
import { Users } from "@gogive/types";
import posthog from "posthog-js";
import { useEffect, useRef, type ReactNode } from "react";
import { isPostHogInitialized } from "@/components/posthog-provider";
import {
  isInternalCaptureBlocked,
  isInternalPostHogUser,
  persistInternalCaptureBlock,
} from "@/lib/posthog-internal-user";
import Loader from "@/components/Loader";
import { useMe } from "@/hooks/useMe";
import { infoToast } from "@/lib/toast";
import { authorize, signIn, signOut } from "@/services/auth.service";
import { toast } from "sonner";

function useCharityAuthExtra(authenticated: boolean) {
  const { user, refresh } = useMe(authenticated);
  return {
    user: user as Users | undefined,
    refreshMe: refresh,
  };
}

const { AuthProvider: OtpAuthProvider, useAuth } = createOtpAuthProvider<
  Users,
  { user: Users | undefined; refreshMe: () => Promise<unknown> }
>({
  signIn,
  signOut,
  authorize,
  isPublicPath: (p) =>
    !!(p?.includes("/signin") || p?.includes("/signup")),
  verifyPathIncludes: "verify",
  buildVerifyUrl: (email) =>
    `/signin/verify?email=${encodeURIComponent(email)}`,
  homePath: "/",
  signInRedirectPath: "/signin",
  notify: {
    info: infoToast,
    error: (message) => toast.error(message),
  },
  messages: {
    sessionExpired: "Your session has ended, please sign in again.",
    verifyMissingEmail: "Redirecting to signin",
    welcomeWithFirstName: (firstName) => `Hi ${firstName}!`,
  },
  syncEmailFromSearchParams: true,
  autoAuthorizeSearchParams: true,
  redirectVerifyWithoutEmail: true,
  authorizeSkipsVerifyEmailGuard: true,
  useExtra: useCharityAuthExtra,
  gateProtectedRoutes: true,
  protectedRouteFallback: (
    <div className="fixed inset-0 flex h-screen w-screen items-center justify-center bg-black/40">
      <div className="text-primary">
        <Loader size={77} />
      </div>
    </div>
  ),
});

function PostHogIdentity({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogInitialized()) return;

    if (!user) {
      if (identifiedUserId.current) {
        if (!isInternalCaptureBlocked()) {
          posthog.reset();
        }
        identifiedUserId.current = null;
      }
      return;
    }

    const distinctId = String(user.id);
    const accountName = user.account?.name ?? "";
    const identityKey = `${distinctId}:${accountName}`;
    if (identifiedUserId.current === identityKey) return;

    if (
      isInternalPostHogUser(user) ||
      isInternalCaptureBlocked()
    ) {
      persistInternalCaptureBlock();
      posthog.opt_out_capturing();
      posthog.stopSessionRecording();
      identifiedUserId.current = identityKey;
      return;
    }

    if (
      identifiedUserId.current &&
      !identifiedUserId.current.startsWith(`${distinctId}:`)
    ) {
      posthog.reset();
    }

    posthog.identify(distinctId, {
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      role: user.role,
      account_id: user.accountId,
      account_name: accountName || undefined,
    });
    if (user.accountId) {
      posthog.group("charity", user.accountId, {
        name: accountName || undefined,
      });
    }
    identifiedUserId.current = identityKey;
  }, [user]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <OtpAuthProvider>
      <PostHogIdentity>{children}</PostHogIdentity>
    </OtpAuthProvider>
  );
}

export { useAuth };
